import { useEffect, useState } from 'react';

import { has } from 'lodash';

import { setFormError } from 'utils/form';
import { unMaskFormFields } from 'utils/unmask';

import { useHistory } from 'react-router';

import { useTranslation } from 'react-i18next';

import { ApolloError, useLazyQuery, useMutation } from '@apollo/client';

import { useForm, SubmitHandler, ErrorOption } from 'react-hook-form';

import { Button } from 'ui';
import useToastContext from 'ui/hooks/useToast';
import { ToastProps } from 'ui/contexts/overlay/Toast';
import { useLoading } from 'ui/contexts/overlay/Loading';

import { Page } from 'dashboard/components/dashboard/Breadcrumbs';
import { Dashboard, DashboardMainHeaderForm } from 'dashboard/components/dashboard';

import { Contacts, Entities } from 'contactGroups/models/contactGroups';

import ContactGroupsFormFieldsComponent, {
  ContactGroupsFormErrors,
  ContactGroupsFormFields,
  ContactGroupsFormInputs,
} from 'contactGroups/components/form/ContactGroupsFormFields';

import {
  ContactGroupCreated,
  ContactGroupMutationVariables,
  CONTACT_GROUP_CREATE_MUTATION,
} from 'contactGroups/graphql/contactGroupsCreateMutation';

import {
  ContactGroupList,
  CONTACT_GROUPS_QUERY,
  contactGroupsTypename,
} from 'contactGroups/graphql/contactGroupsQuery';

import {
  CONSUMER_UNITS_QUERY,
  ConsumerUnitsList,
} from 'consumerUnits/graphql/consumerUnitsQuery';

import {
  CooperativesSelectList,
  COOPERATIVES_SELECT_QUERY,
  COOPERATIVES_SELECT_QUERY_VARIABLES,
} from 'cooperatives/graphql/cooperativesSelectQuery';

import {
  GenerationUnitsSelectList,
  GENERATION_UNITS_SELECT_QUERY,
  GENERATION_UNITS_SELECT_QUERY_VARIABLES,
} from 'generationUnits/graphql/generationUnitsSelectQuery';

import { CONSUMER_UNITS_SELECT_QUERY_VARIABLES } from 'consumerUnits/graphql/consumerUnitsSelectQuery';
import {
  COOPERATIVE_MEMBERS_SELECT_QUERY,
  COOPERATIVE_MEMBERS_SELECT_QUERY_VARIABLES,
  CooperativeMembersSelectList,
} from 'cooperatives/graphql/cooperativeMembersSelectQuery';

const DASHBOARD_CONTACT_GROUPS_ROUTE = '/dashboard/contact-groups';

const BREADCRUMB_PAGES: Page[] = [
  {
    current: false,
    name: 'Grupo & Contatos',
    route: DASHBOARD_CONTACT_GROUPS_ROUTE,
  },
  {
    route: null,
    current: true,
    name: 'Novo Grupo & Contatos',
  },
];
const TITLE = 'Novo Grupo & Contatos';

const CREATE_ERROR_TOAST: ToastProps = {
  variant: 'danger',
  title: 'Algo deu errado!',
  text: 'Houve um erro ao tentar criar o cadastro de Grupo & Contatos',
};

const CREATE_SUCCESS_TOAST: ToastProps = {
  title: 'Sucesso!',
  variant: 'primary',
  text: 'Sucesso ao cadastrar Grupo & Contatos',
};

export default function CreateContactGroupsPage() {
  const { push } = useHistory();
  const { t } = useTranslation();
  const { addToast } = useToastContext();
  const { showLoading, closeLoading, LoadingOverlay } = useLoading();

  const {
    reset,
    control,
    register,
    setError,
    setValue,
    clearErrors,
    handleSubmit,
    formState: { errors },
  } = useForm<ContactGroupsFormInputs>();

  const [contactList, setContactList] = useState<Contacts>({
    owner: [],
    domain: [],
    financial: [],
    juridical: [],
    technical: [],
    administrative: [],
  });

  const [entitiesList, setEntitiesList] = useState<Entities>({
    cooperative: [],
    consumerUnit: [],
    generationUnit: [],
  });

  const [
    consumerUnitsSelect,
    { data: consumerUnitsSelectData, loading: consumerUnitsSelectLoading },
  ] = useLazyQuery<ConsumerUnitsList>(
    CONSUMER_UNITS_QUERY,
    CONSUMER_UNITS_SELECT_QUERY_VARIABLES
  );

  const [
    cooperativesSelect,
    { data: cooperativesSelectData, loading: cooperativesSelectLoading },
  ] = useLazyQuery<CooperativesSelectList>(
    COOPERATIVES_SELECT_QUERY,
    COOPERATIVES_SELECT_QUERY_VARIABLES
  );

  const [
    generationUnitsSelect,
    { data: generationUnitsSelectData, loading: generationUnitsSelectLoading },
  ] = useLazyQuery<GenerationUnitsSelectList>(
    GENERATION_UNITS_SELECT_QUERY,
    GENERATION_UNITS_SELECT_QUERY_VARIABLES
  );

  const [
    cooperativeMembersSelect,
    { data: cooperativeMembersSelectData, loading: cooperativeMembersSelectLoading },
  ] = useLazyQuery<CooperativeMembersSelectList>(
    COOPERATIVE_MEMBERS_SELECT_QUERY,
    COOPERATIVE_MEMBERS_SELECT_QUERY_VARIABLES
  );

  useEffect(() => {
    consumerUnitsSelect();
  }, [consumerUnitsSelect]);

  useEffect(() => {
    cooperativesSelect();
  }, [cooperativesSelect]);

  useEffect(() => {
    generationUnitsSelect();
  }, [generationUnitsSelect]);

  useEffect(() => {
    cooperativeMembersSelect();
  }, [cooperativeMembersSelect]);

  const [contactGroupCreateMutation, { loading: loadingCreate }] = useMutation<
    ContactGroupCreated,
    ContactGroupMutationVariables
  >(CONTACT_GROUP_CREATE_MUTATION, {
    onError(error: ApolloError) {
      if (has(error.graphQLErrors[0], 'message')) {
        addToast(CREATE_ERROR_TOAST);
      }

      setFormError(
        error,
        (field: string, error: ErrorOption) => {
          setError(field as keyof ContactGroupsFormFields, error);

          setTimeout(() => clearErrors(), 2500);
        },
        t
      );
    },
    onCompleted() {
      addToast(CREATE_SUCCESS_TOAST);
      push(DASHBOARD_CONTACT_GROUPS_ROUTE);
    },
  });

  const onSubmit: SubmitHandler<ContactGroupsFormFields> = (
    contactGroupCreateInput,
    event
  ) => {
    event?.preventDefault();

    contactGroupCreateInput = Object.assign(contactGroupCreateInput, {
      contacts: contactList,
      entities: entitiesList,
    });

    contactGroupCreateInput = unMaskFormFields(contactGroupCreateInput);

    const hasLeastOneEntity =
      entitiesList?.cooperative?.length ||
      entitiesList?.consumerUnit?.length ||
      entitiesList?.generationUnit?.length;

    const hasLeastOneDomainContact = contactList?.domain?.length;

    if (!hasLeastOneDomainContact) {
      setError('contacts', {
        type: 'manual',
        message: 'Este campo precisa ser preenchido',
      });
    }

    if (!hasLeastOneEntity) {
      setError('entities', {
        type: 'manual',
        message: 'Você precisa vincular pelo menos uma entidade ao grupo',
      });
    }

    setTimeout(() => clearErrors(), 3000);

    if (hasLeastOneEntity && hasLeastOneDomainContact) {
      contactGroupCreateMutation({
        variables: {
          contactGroupCreateInput: {
            contacts: contactGroupCreateInput.contacts,
            entities: contactGroupCreateInput.entities,
            groupName: contactGroupCreateInput.groupName,
          },
        },
        update(cache, { data }) {
          const existingData = cache.readQuery<ContactGroupList>({
            query: CONTACT_GROUPS_QUERY,
          });
          cache.writeQuery({
            query: CONTACT_GROUPS_QUERY,
            data: {
              contactGroups: {
                __typename: contactGroupsTypename,
                afterCursor: existingData?.contactGroups.afterCursor || null,
                beforeCursor: existingData?.contactGroups.beforeCursor || null,
                entries: [
                  data?.contactGroupCreate,
                  ...(existingData?.contactGroups.entries || []),
                ],
              },
            },
          });
        },
      });
    }
  };

  const isLoading =
    loadingCreate ||
    cooperativesSelectLoading ||
    consumerUnitsSelectLoading ||
    generationUnitsSelectLoading ||
    cooperativeMembersSelectLoading;

  useEffect(() => {
    if (isLoading) {
      return showLoading();
    }

    closeLoading();
  }, [isLoading, showLoading, closeLoading]);

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <LoadingOverlay />
      <Dashboard
        dashboardMainHeaderTitle={
          <DashboardMainHeaderForm breadcrumbPages={BREADCRUMB_PAGES} title={TITLE}>
            <Button type="submit" size="sm" disabled={isLoading}>
              Salvar
            </Button>
          </DashboardMainHeaderForm>
        }
      >
        <ContactGroupsFormFieldsComponent
          reset={reset}
          control={control}
          setValue={setValue}
          register={register}
          formErrors={errors as ContactGroupsFormErrors}
          cooperatives={cooperativesSelectData?.cooperatives?.entries || []}
          consumerUnits={consumerUnitsSelectData?.consumerUnits?.entries || []}
          generationUnits={generationUnitsSelectData?.generationUnits?.entries || []}
          cooperativeMembers={
            cooperativeMembersSelectData?.cooperativeMembers?.entries || []
          }
          handleGetEntityList={(entities: Entities) => setEntitiesList(entities)}
          handleGetContactList={(contacts: Contacts) => setContactList(contacts)}
        />
      </Dashboard>
    </form>
  );
}
